/**
 * Copyright 2025 Beijing Volcano Engine Technology Co., Ltd. All Rights Reserved.
 * SPDX-license-identifier: BSD-3-Clause
 */

/**
 * Live2DAvatar
 * -------------
 * A real Live2D (Cubism 4) avatar rendered with pixi-live2d-display.
 *
 * Unlike a CSS-manipulated flat image (which tears when the mouth / eyes move),
 * this component loads a proper Cubism model with a deformation mesh, so
 * the mouth / eyes deform without tearing.
 *
 * Driving signals (reused from the already-working RTC pipeline):
 *   - isTalking  : whether the bot is currently speaking (room.isAITalking)
 *   - volume     : real-time linearVolume (0~255-ish) of the speaking party
 * When talking, `volume` drives ParamMouthOpenY for lip-sync. When idle, the
 * model's own idle motion + auto eye-blink + breathing keep it alive.
 *
 * Requirements:
 *   - Cubism Core runtime must be loaded globally via <script> in public/index.html
 *     (window.Live2DCubismCore). pixi-live2d-display reads it at model load time.
 */

import { useEffect, useRef } from 'react';

// Import the Cubism4 bundle so the Cubism4 runtime factory is registered.
// eslint-disable-next-line import/no-extraneous-dependencies
import { Live2DModel } from 'pixi-live2d-display/cubism4';
// eslint-disable-next-line import/no-extraneous-dependencies
import * as PIXI from 'pixi.js';

// pixi-live2d-display needs access to PIXI.Ticker for its internal auto-update
// registration and to the interaction plugin. Expose PIXI on window.
(window as unknown as { PIXI: typeof PIXI }).PIXI = PIXI;
Live2DModel.registerTicker(PIXI.Ticker);

export interface Live2DAvatarProps {
  /** Absolute or public-relative URL to the *.model3.json */
  modelUrl?: string;
  /** Whether the character is currently speaking; enables volume-driven lip-sync */
  isTalking?: boolean;
  /** Real-time linear volume (~0..255). Only used while isTalking. */
  volume?: number;
  /** CSS class for the wrapping element */
  className?: string;
  /** Inline style for the wrapping element */
  style?: React.CSSProperties;
  /** Fit the model into the canvas; 'cover' fills, 'contain' shows the whole model */
  fit?: 'cover' | 'contain';
  /** Vertical framing: how much of the model height to show, 1 = full body */
  zoom?: number;
  /** Reports the rendered model rect (fractions of the container) so callers can
   *  anchor overlays (e.g. a resize handle) to the visible character. */
  onContentRect?: (rect: {
    left: number;
    top: number;
    width: number;
    height: number;
  }) => void;
}

const DEFAULT_MODEL_URL = '/live2d/models/Hiyori/Hiyori.model3.json';

/** linearVolume looks like 0~255; map to a 0..1 mouth-open amount with a talk floor. */
function normalizeVolume(vol: number): number {
  const v = Math.max(0, Math.min(255, vol));
  // Threshold ~4 so soft TTS still drives the mouth; ~76 saturates.
  return Math.max(0, Math.min(1, (v - 4) / 72));
}

function Live2DAvatar({
  modelUrl = DEFAULT_MODEL_URL,
  isTalking = false,
  volume = 0,
  className,
  style,
  fit = 'contain',
  zoom = 1,
  onContentRect,
}: Live2DAvatarProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const appRef = useRef<PIXI.Application | null>(null);
  const modelRef = useRef<Live2DModel | null>(null);
  const roRef = useRef<ResizeObserver | null>(null);
  const onContentRectRef = useRef(onContentRect);
  onContentRectRef.current = onContentRect;

  // Latest driving signals, read inside the render loop without re-subscribing.
  const talkingRef = useRef(isTalking);
  const volumeRef = useRef(volume);
  // Smoothed mouth value driven by real RTC audio volume.
  const mouthRef = useRef(0);
  const sawUsableVolumeRef = useRef(false);
  const talkingStartedAtRef = useRef(0);
  const wasTalkingRef = useRef(isTalking);

  useEffect(() => {
    talkingRef.current = isTalking;
  }, [isTalking]);
  useEffect(() => {
    volumeRef.current = volume;
  }, [volume]);

  // Create the PIXI app + load the model once (or when modelUrl changes).
  useEffect(() => {
    let disposed = false;
    const canvas = canvasRef.current;
    if (!canvas) return undefined;

    const app = new PIXI.Application({
      view: canvas,
      autoStart: true,
      resizeTo: canvas.parentElement || undefined,
      backgroundAlpha: 0,
      antialias: true,
      // Cap resolution on HiDPI to keep it light on GPU-less machines.
      resolution: Math.min(window.devicePixelRatio || 1, 2),
      autoDensity: true,
    });
    appRef.current = app;

    const layout = () => {
      const model = modelRef.current;
      if (!model || !app.renderer) return;
      const w = app.renderer.width / app.renderer.resolution;
      const h = app.renderer.height / app.renderer.resolution;
      const mw = model.internalModel.originalWidth;
      const mh = model.internalModel.originalHeight;
      if (!mw || !mh) return;
      const scaleContain = Math.min(w / mw, h / mh);
      const scaleCover = Math.max(w / mw, h / mh);
      const base = fit === 'cover' ? scaleCover : scaleContain;
      const scale = base * zoom;
      model.scale.set(scale);
      // Anchor at top-center: keep the face in view, let the body run off-canvas.
      model.anchor.set(0.5, 0);
      model.position.set(w / 2, 0);

      // Report the visible model rect (top-center anchored; body may run off the
      // bottom, so clamp the height to the canvas) for overlay anchoring.
      const report = onContentRectRef.current;
      if (report) {
        const modelW = scale * mw;
        const modelH = scale * mh;
        const left = (w - modelW) / 2;
        const clamp01 = (v: number) => Math.max(0, Math.min(1, v));
        report({
          left: clamp01(left / w),
          top: 0,
          width: clamp01(modelW / w),
          height: clamp01(Math.min(modelH, h) / h),
        });
      }
    };

    Live2DModel.from(modelUrl, { autoUpdate: false })
      .then((model) => {
        if (disposed) {
          model.destroy();
          return;
        }
        modelRef.current = model;
        app.stage.addChild(model);
        // Disable built-in interaction focus so the eyes don't chase the cursor
        // in the pre-call screen; can be re-enabled later if desired.
        model.interactive = false;
        layout();

        // Compute the smoothed mouth-open value from the RTC volume signal.
        // Called each frame right before the internal model commits parameters
        // to the mesh, so our value is the *last* write and lip-sync sticks.
        const computeMouth = (now: number) => {
          const talking = talkingRef.current;
          const rawVolume = volumeRef.current;
          if (talking && !wasTalkingRef.current) {
            talkingStartedAtRef.current = now;
            sawUsableVolumeRef.current = false;
          }
          if (!talking) {
            sawUsableVolumeRef.current = false;
          }
          wasTalkingRef.current = talking;

          const volumeMouth = normalizeVolume(rawVolume);
          if (talking && rawVolume > 4) {
            sawUsableVolumeRef.current = true;
          }
          // Prefer actual RTC volume. Only if no usable audio-volume report has
          // arrived for this utterance do a small fallback pulse; once volume is
          // available, quiet syllable gaps close the mouth naturally.
          const missingVolumeFallback =
            talking && !sawUsableVolumeRef.current && now - talkingStartedAtRef.current > 260
              ? 0.18 + 0.22 * Math.abs(Math.sin(now * 0.026))
              : 0;
          const target = talking ? Math.max(volumeMouth, missingVolumeFallback) : 0;
          // Fast attack / release so open-close follows the 200ms RTC volume report.
          const k = target > mouthRef.current ? 0.82 : 0.55;
          mouthRef.current += (target - mouthRef.current) * k;
          return mouthRef.current;
        };

        // Override the mouth INSIDE the model update, after motion/physics have
        // written ParamMouthOpenY but before `coreModel.update()` commits it to
        // the mesh. Setting it after `im.update()` (as before) was too late: the
        // next frame's Idle motion overwrote it before the commit, so the mouth
        // never actually moved.
        const im = model.internalModel;
        im.on('beforeModelUpdate', () => {
          const mouth = computeMouth(performance.now());
          try {
            (
              im.coreModel as { setParameterValueById: (id: string, v: number) => void }
            ).setParameterValueById('ParamMouthOpenY', mouth);
          } catch (e) {
            // Some models use a different id; ignore silently.
          }
        });

        // Manual update loop; the mouth override runs via the event above.
        app.ticker.add(() => {
          im.update(app.ticker.deltaMS, performance.now());
        });

        // Start an idle motion so the character isn't frozen.
        model.motion('Idle').catch(() => {});
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.error('[Live2DAvatar] failed to load model:', modelUrl, err);
      });

    // Relayout on container resize.
    if (canvas.parentElement && 'ResizeObserver' in window) {
      const ro = new ResizeObserver(() => layout());
      ro.observe(canvas.parentElement);
      roRef.current = ro;
    }

    return () => {
      disposed = true;
      roRef.current?.disconnect();
      roRef.current = null;
      if (modelRef.current) {
        modelRef.current.destroy({ children: true, texture: true, baseTexture: true });
        modelRef.current = null;
      }
      app.destroy(false, { children: true, texture: true, baseTexture: true });
      appRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modelUrl, fit, zoom]);

  return (
    <div
      className={className}
      style={{ position: 'relative', width: '100%', height: '100%', ...style }}
    >
      <canvas ref={canvasRef} style={{ width: '100%', height: '100%', display: 'block' }} />
    </div>
  );
}

export default Live2DAvatar;
